/**
 * Filling a column from a formula somebody writes.
 *
 * The sibling of the worked-out column modal, which offers a fixed list of
 * operations. This is the other half: the things nobody can put on a list,
 * because they are arithmetic particular to one file. Both write the same kind
 * of rule and both are worked out as the file is read, so neither costs
 * anything until the sheet is saved.
 *
 * The formula is checked against the file as it is typed, and the button stays
 * disabled until it compiles. Saying "this file has no column called prce"
 * while the cursor is still in the box is the entire difference between a
 * formula box people use and one they give up on.
 */
import { useEffect, useState } from 'react';
import {
  Alert, Button, Code, Group, Modal, Stack, Text, Textarea, TextInput,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useAddFormulaColumn, useCheckFormula } from '../api/queries';

/** Shown under the box, so the syntax does not have to be guessed or documented elsewhere. */
const EXAMPLES = [
  { formula: '[price] * [qty] * 1.2', does: 'Arithmetic, with VAT added.' },
  { formula: 'IF([status] = "sold", [revenue], 0)', does: 'One value or another.' },
  // eslint-disable-next-line no-restricted-syntax -- worked example over a spreadsheet's own [leads] column
  { formula: 'ROUND([spend] / [leads], 2)', does: 'Cost per lead, to two places.' },
  { formula: 'CONCAT([first], " ", [last])', does: 'Text joined together.' },
  {
    formula: 'MD5(LOWER(TRIM([email])))',
    does: 'The digest a suppression list is exchanged as. Lower and trim go inside the hash, not around it.',
  },
];

export function SheetFormulaModal({
  sheetId,
  columns,
  startFrom,
  baseVersion,
  opened,
  onClose,
}: {
  sheetId: string | null;
  columns: string[];
  /** The column whose menu this was opened from, offered as a starting point. */
  startFrom: string | null;
  /** The version this was written against, so a stale change is refused. */
  baseVersion: number;
  opened: boolean;
  onClose: () => void;
}) {
  const add = useAddFormulaColumn(sheetId);
  const [name, setName] = useState('');
  const [formula, setFormula] = useState('');
  // Checked a beat after typing stops. Every keystroke would ask the server
  // about a formula that is still half written.
  const [settled] = useDebouncedValue(formula, 400);
  const check = useCheckFormula(sheetId, name, settled);

  useEffect(() => {
    if (!opened) return;
    setName('');
    setFormula(startFrom ? `[${startFrom}]` : '');
  }, [opened, startFrom]);

  // Only trust an answer about the formula on screen. While the debounce is
  // catching up, the last answer is about older text.
  const current = settled === formula && !check.isFetching;
  const compiles = current && check.data?.ok === true;
  const problem = current && check.data?.ok === false ? check.data.error : '';

  const replacing = columns.some((c) => c === name.trim());

  function submit() {
    add.mutate(
      { name: name.trim(), formula, baseVersion },
      {
        onSuccess: () => {
          onClose();
          notifications.show({
            color: 'teal',
            title: `${name.trim()} filled in`,
            message: 'Worked out as the file is read. Apply to file to keep it.',
          });
        },
        onError: (e) =>
          notifications.show({
            color: 'red',
            title: 'Could not use that formula',
            message: (e as Error).message,
          }),
      },
    );
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Fill a column with a formula" size="lg">
      <Stack>
        <Textarea
          label="Formula"
          description="Column names go in square brackets. Text goes in quotes."
          placeholder="[price] * [qty]"
          value={formula}
          onChange={(e) => setFormula(e.currentTarget.value)}
          autosize
          minRows={2}
          maxRows={6}
          error={problem || undefined}
          styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
        />

        {compiles && check.data && check.data.sources.length > 0 && (
          <Text size="xs" c="dimmed" mt={-8}>
            Reads {check.data.sources.join(', ')}.
          </Text>
        )}

        <TextInput
          label="Column to fill"
          description={
            replacing
              ? 'This file already has that column, so the formula replaces what is in it.'
              : 'A name this file does not have yet adds a column.'
          }
          placeholder="e.g. Total"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && name.trim() && compiles) submit();
          }}
        />

        {replacing && (
          <Alert color="chartAmber" variant="light">
            <Text size="sm">
              {name.trim()} already exists. The formula replaces what it holds, and cannot
              read the column it is filling. The version before this is kept, so the old
              values can be got back.
            </Text>
          </Alert>
        )}

        <Stack gap={4}>
          <Text size="xs" c="dimmed">Some things it understands:</Text>
          {EXAMPLES.map((e) => (
            <Group key={e.formula} gap="xs" wrap="nowrap" align="baseline">
              <Code style={{ whiteSpace: 'nowrap' }}>{e.formula}</Code>
              <Text size="xs" c="dimmed">{e.does}</Text>
            </Group>
          ))}
          <Text size="xs" c="dimmed" mt={4}>
            Also AND, OR, comparisons, and UPPER, LOWER, TRIM, LEFT, RIGHT, LEN, MD5 and
            SHA256. A row whose
            sums do not work out, such as text where a number belongs or a division by
            zero, is left blank rather than counted as nothing.
          </Text>
        </Stack>

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button loading={add.isPending} disabled={!name.trim() || !compiles} onClick={submit}>
            {replacing ? 'Replace the column' : 'Add the column'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
